import React, { useContext, useState, useEffect } from 'react';

import { Form } from 'semantic-ui-react';

import _get from 'lodash/get';
import { useSelector } from 'react-redux';

import { VIDEO_BLOCK_ELEMENT_TYPE } from 'components/modules/custom-pages/constants/types';
import { VideoBlockPropType } from 'components/modules/custom-pages/types/video-block.types';
import { useLatestRefValue } from 'components/modules/utils/hooks';
import VideoPlayerEditor from 'components/modules/video/VideoPlayerEditor';
import {
  SOURCE_OTHER,
  SOURCE_VIMEO,
  SOURCE_YOUTUBE,
} from 'components/modules/video/VideoPlayerEditor/VideoPlayerEditor.constants';
import { selectBusinessFiles } from 'selectors/business';

import {
  createComponentConfiguration,
  updateComponentConfiguration,
  updateVideoComponentFromSave,
} from './EditVideoBlock.utils';
import { EditPageElementModalContext } from '../../EditPageElementModal.context';

import './EditVideoBlock.scss';

const propTypes = {
  element: VideoBlockPropType.isRequired,
};

const defaultProps = {};

export default function EditVideoBlock({ element }) {
  const { page, registerCallback, onUpdateElementState } = useContext(EditPageElementModalContext);

  const { videoId, source, url, placeholder } = element;
  const businessFiles = useSelector(selectBusinessFiles);
  const placeholderFile = businessFiles.find(({ file }) => file === placeholder);
  const [data, setData] = useState({ videoId, source, url, placeholderFile });

  const latestElementRef = useLatestRefValue(element);
  const latestDataRef = useLatestRefValue(data);

  function onChange(e, changes) {
    const currentElement = latestElementRef.current;
    const changedPlaceholder = _get(changes, 'placeholderFile.file', undefined);
    const nextElement = {
      ...currentElement,
      ...changes,
      placeholder: changedPlaceholder,
    };
    setData((prevData) => ({ ...prevData, ...changes }));
    onUpdateElementState(VIDEO_BLOCK_ELEMENT_TYPE, nextElement);
  }

  async function onSaveContent() {
    const latestData = latestDataRef.current;
    const currentElement = latestElementRef.current;
    const { componentConfigurationId } = currentElement;
    const { id: pageId } = page;

    const payload = {
      data: {
        video: {
          videoId: _get(latestData, 'videoId'),
          source: _get(latestData, 'source'),
          url: _get(latestData, 'url'),
          placeholder: _get(latestData.placeholderFile, 'file', undefined),
        },
      },
    };

    if (componentConfigurationId) {
      await updateComponentConfiguration(componentConfigurationId, payload);

      return currentElement;
    }

    const result = await createComponentConfiguration(pageId, payload);
    return updateVideoComponentFromSave(currentElement, businessFiles, result);
  }

  useEffect(() => {
    registerCallback(VIDEO_BLOCK_ELEMENT_TYPE, onSaveContent);
  }, []);

  return (
    <div className="edit-video-block">
      <Form>
        <VideoPlayerEditor
          videoId={_get(data, 'videoId')}
          source={_get(data, 'source')}
          url={_get(data, 'url')}
          placeholderFile={_get(data, 'placeholderFile')}
          videoSources={[SOURCE_VIMEO, SOURCE_YOUTUBE, SOURCE_OTHER]}
          onChange={onChange}
        />
      </Form>
    </div>
  );
}

EditVideoBlock.propTypes = propTypes;
EditVideoBlock.defaultProps = defaultProps;
